import React, { useState } from 'react';

import { Box, Typography } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../pages/types';
import EditableMarkdownText from './editable-markdown-text';

interface HeroProps extends CommonComponentProps {
  title?: string;
  subtitle?: string;
  backgroundImage?: ImageObject;
  videoSrc?: string;
}

const Hero: React.FC<HeroProps> = ({
  id,
  title = '',
  subtitle: paragraph = '',
  videoSrc,
  sx,
  isEditable,
  onPropsChange,
  onImageUpload,
}) => {
  const [videoLoaded, setVideoLoaded] = useState(false);

  const handleTextChange = (key: keyof HeroProps, value: string) => {
    if (onPropsChange) {
      onPropsChange({ [key]: value });
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Only trigger if clicking directly on the background container, not children
    if (
      isEditable &&
      onImageUpload &&
      onPropsChange &&
      e.target === e.currentTarget
    ) {
      onImageUpload((url) => {
        // Update the sx prop which contains the background image
        const newSx = { ...sx };
        if (!newSx.backgroundImage) newSx.backgroundImage = {};
        if (typeof newSx.backgroundImage === 'object') {
          newSx.backgroundImage.url = url;
        } else {
          // If it was a string or undefined, make it an object
          newSx.backgroundImage = { url: url, title: '', altText: '' };
        }
        onPropsChange({ sx: newSx });
      });
    }
  };

  // Extract background image from sx prop if it exists
  const backgroundImage = sx?.backgroundImage;
  const backgroundImageUrl =
    typeof backgroundImage === 'object' && backgroundImage?.url
      ? backgroundImage.url
      : typeof backgroundImage === 'string'
      ? backgroundImage
      : undefined;

  return (
    <Box
      id={id}
      onClick={handleBackgroundClick}
      sx={{
        height: '100vh',
        display: 'flex',
        width: '100vw',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: backgroundImageUrl
          ? `url(${backgroundImageUrl})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        cursor: isEditable ? 'pointer' : 'default',
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          padding: '20px 0',
        }}
      >
        <EditableMarkdownText
          content={title}
          onSave={(val) => handleTextChange('title', val)}
          isEditable={!!isEditable}
          component={Typography}
          typographyVariant="h1"
          sx={{
            maxWidth: '950px',
            padding: '0px 0px 10px 0px',
            cursor: isEditable ? 'text' : 'inherit',
          }}
        />
        <EditableMarkdownText
          content={paragraph}
          onSave={(val) => handleTextChange('subtitle', val)}
          isEditable={!!isEditable}
          component={Typography}
          typographyVariant="h2"
          sx={{
            maxWidth: '700px',
            padding: '0px 20px 0px 20px',
            cursor: isEditable ? 'text' : 'inherit',
          }}
        />
      </Box>
    </Box>
  );
};

export default Hero;
